import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { getAuth, signOut, firebaseReady } from '@/lib/firebase';
import { apiRequest } from '@/lib/queryClient';
import { trackEvent, trackError, EventName, EventCategory, setUserProperties } from '@/lib/analytics';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isLoading: boolean; // Alias for loading for compatibility with RedirectAuth
  signOut: () => Promise<void>;
  syncUserWithBackend: (user: User) => Promise<void>;
  authToken: string | null; // Add authToken to context
  refreshAuthToken: () => Promise<string | null>; // Add method to refresh token
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isLoading: true,
  authToken: null,
  signOut: async () => {},
  syncUserWithBackend: async () => {},
  refreshAuthToken: async () => null,
});

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [prevUser, setPrevUser] = useState<User | null>(null);
  
  // Function to get a fresh ID token from Firebase
  const refreshAuthToken = async (): Promise<string | null> => {
    try {
      if (!user) return null;

      if (process.env.NODE_ENV === 'development') {
        console.log("Refreshing auth token for user:", user.uid);
      } else {
        console.log("Authentication: Refreshing token");
      }
      
      const token = await user.getIdToken(true); // Force refresh
      
      if (process.env.NODE_ENV === 'development') {
        console.log("Token refreshed, length:", token.length);
      }
      
      setAuthToken(token);

      // Update global token for queryClient
      if (typeof window !== 'undefined') {
        window.__AUTH_TOKEN = token;
      }

      return token;
    } catch (error) {
      console.error('Error refreshing auth token:', error);
      trackError(error as Error, {
        category: EventCategory.Auth,
        action: 'refresh_token',
        userId: user?.uid
      });
      return null;
    }
  };

  // Sync Firebase user with our backend
  const syncUserWithBackend = async (user: User) => {
    const startTime = performance.now();
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log("Starting syncUserWithBackend for user:", user.uid);
      } else {
        console.log("Authentication: Starting backend sync");
      }

      if (!user.uid || !user.email) {
        console.error("Missing required user data for backend sync:", {
          hasUid: !!user.uid,
          hasEmail: !!user.email
        });
        throw new Error("Missing required user data for backend sync");
      }

      // Get the ID token
      let idToken: string;
      try {
        idToken = await user.getIdToken();
        
        if (process.env.NODE_ENV === 'development') {
          console.log("Got idToken, length:", idToken.length);
        }

        // Store the token in state for other components to use
        setAuthToken(idToken);

        // Update global token for queryClient
        if (typeof window !== 'undefined') {
          window.__AUTH_TOKEN = idToken;
        }

        if (!idToken) {
          throw new Error("Failed to get ID token from Firebase");
        }
      } catch (tokenError: any) {
        console.error('Error getting Firebase ID token:', tokenError);
        trackError(tokenError, {
          category: EventCategory.Auth,
          action: 'get_id_token',
          userId: user.uid
        });
        throw new Error(`Failed to get Firebase ID token: ${tokenError.message}`);
      }
      
      // Prepare the payload
      const payload = {
        idToken,
        email: user.email,
        displayName: user.displayName || user.email?.split('@')[0] || 'User',
        photoURL: user.photoURL,
        uid: user.uid,
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log("Sending user data to backend:", {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || user.email?.split('@')[0] || 'User'
        });
      }
      
      // Call the API
      let response;
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log("Making API request to /api/auth/session");
        }
        
        response = await apiRequest('POST', '/api/auth/session', payload);
        
        // Parse the successful response
        const responseData = await response.json();
        
        if (process.env.NODE_ENV === 'development') {
          console.log("Backend sync successful:", responseData);
        }
        
        // Track successful authentication
        trackEvent(EventName.SignIn, {
          method: user.providerData?.[0]?.providerId || 'unknown',
          is_new_user: !responseData.user.lastLoginAt,
          user_id: user.uid
        });
        
        // Set user properties for analytics
        setUserProperties(user, {
          user_type: responseData.user.role || 'standard',
          subscription_plan: responseData.user.subscriptionPlan || 'free'
        });
        
        const endTime = performance.now();
        trackEvent('auth_sync_completed', {
          duration_ms: Math.round(endTime - startTime),
          success: true
        });
        
        return responseData;
      } catch (apiError: any) {
        console.error('API error during backend sync:', apiError);
        console.error('API error details:', {
          message: apiError.message,
          status: apiError.status,
          statusText: apiError.statusText
        });
        
        // Track API error
        trackError(apiError, {
          category: EventCategory.Auth,
          action: 'backend_sync_api',
          status: apiError.status,
          userId: user.uid
        });
        
        // Check if this is a network error (likely a connection issue)
        if (apiError.message && (
            apiError.message.includes('NetworkError') || 
            apiError.message.includes('Failed to fetch') ||
            apiError.message.includes('Network request failed')
        )) {
          throw new Error(`Network error: Cannot connect to server. Please check your internet connection.`);
        }
        
        throw apiError;
      }
    } catch (error: any) {
      console.error('Error syncing user with backend:', error);
      // Add detailed logging for debugging
      console.error('Sync error details:', {
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack,
        user: {
          uid: user.uid,
          email: user.email,
          isEmailVerified: user.emailVerified,
          displayName: user.displayName,
          hasPhotoURL: !!user.photoURL,
          providerId: user.providerId,
          providerData: user.providerData?.map(p => ({ 
            providerId: p.providerId,
            hasEmail: !!p.email
          }))
        }
      });
      
      const endTime = performance.now();
      trackEvent('auth_sync_completed', {
        duration_ms: Math.round(endTime - startTime),
        success: false,
        error_type: error.name,
        error_message: error.message.substring(0, 100) // Truncate long messages
      });
      
      throw error; // Re-throw to allow handling in the calling function
    }
  };

  // Set up a token refresh interval to proactively refresh tokens before they expire
  useEffect(() => {
    if (!user) return;
    
    // Refresh token every 45 minutes (Firebase tokens expire after 1 hour)
    const TOKEN_REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes in milliseconds
    
    
    const refreshInterval = setInterval(async () => {
      try {
        console.log("Proactively refreshing auth token");
        await refreshAuthToken();
      } catch (error) {
        console.error("Failed to proactively refresh token:", error);
      }
    }, TOKEN_REFRESH_INTERVAL);
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [user]);

  useEffect(() => {
    // Initialize auth state listener after Firebase is ready
    let unsubscribe: () => void = () => {};
    
    // Async function to set up auth state listener
    const setupAuthListener = async () => {
      try {
        // Wait for Firebase to initialize
        await firebaseReady;
        
        // Get auth instance
        const auth = await getAuth();
        
        // Now set up the listener
        unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (process.env.NODE_ENV === 'development') {
        console.log("Auth state changed, user:", user ? user.uid : "null");
      } else {
        console.log("Authentication: State changed to", user ? "authenticated" : "unauthenticated");
      }
      
      // Track auth state change
      if (user) {
        trackEvent('auth_state_changed', {
          state: 'signed_in',
          provider: user.providerData?.[0]?.providerId || 'unknown'
        });
      } else if (prevUser) {
        trackEvent('auth_state_changed', {
          state: 'signed_out'
        });
      }
      
      // Update previous user state for next time
      setPrevUser(user);

      if (user) {
        try {
          // Get a fresh token immediately when user changes
          let token: string;
          try {
            token = await user.getIdToken(true);
            setAuthToken(token);

            // Make token globally available for queryClient
            if (typeof window !== 'undefined') {
              window.__AUTH_TOKEN = token;
              // Also expose the refresh function globally
              window.__REFRESH_AUTH_TOKEN = refreshAuthToken;
            }

            if (process.env.NODE_ENV === 'development') {
              console.log("Set initial auth token, length:", token.length);
            }
          } catch (tokenError) {
            console.error("Failed to get initial ID token:", tokenError);
            trackError(tokenError as Error, {
              category: EventCategory.Auth,
              action: 'initial_token',
              userId: user.uid
            });
            // Set loading to false and return early if token fails
            setLoading(false);
            return;
          }

          // Sync with the backend BEFORE setting user state
          // This ensures the session is established before components make API calls
          await syncUserWithBackend(user);
          
          // Only set the user state AND loading to false AFTER backend sync completes
          // This prevents race conditions where components make API calls before session is ready
          setUser(user);
          setLoading(false);
        } catch (error) {
          console.error("Error during auth state change sync:", error);
          trackError(error as Error, {
            category: EventCategory.Auth,
            action: 'state_change_sync',
            userId: user.uid
          });
          // Don't set user state if sync fails, but still stop loading
          setLoading(false);
          return;
        }
      } else {
        // Clear auth token when user is null
        setAuthToken(null);

        // Clear global token as well
        if (typeof window !== 'undefined') {
          window.__AUTH_TOKEN = null;
        }
        
        // Set user to null immediately for sign out
        setUser(null);
        setLoading(false);
      }
    }, (error) => {
          console.error("Firebase auth state observer error:", error);
          trackError(error as Error, {
            category: EventCategory.Auth,
            action: 'observer_error'
          });
          setLoading(false);
        });
      } catch (error) {
        console.error("Error setting up Firebase auth:", error);
        trackError(error as Error, {
          category: EventCategory.Auth,
          action: 'setup_error'
        });
        setLoading(false);
      }
    };
    
    // Start the setup process
    setupAuthListener();

    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      // Track sign out attempt
      trackEvent(EventName.SignOut, {
        user_id: user?.uid
      });
      
      // Call backend to clear session
      await apiRequest('POST', '/api/auth/signout', {});
      
      // Sign out from Firebase - make sure Firebase is initialized
      await signOut();
      
      // Clear user state
      setUser(null);
      setAuthToken(null); // Clear the auth token on sign out

      // Clear global token as well
      if (typeof window !== 'undefined') {
        window.__AUTH_TOKEN = null;
      }
    } catch (error) {
      console.error("Error during sign out:", error);
      trackError(error as Error, {
        category: EventCategory.Auth,
        action: 'sign_out',
        userId: user?.uid
      });
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isLoading: loading,
      signOut: handleSignOut,
      syncUserWithBackend,
      authToken,
      refreshAuthToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
